import merge from "lodash/merge"
import tinycolor from "tinycolor2"
import { StyleSheet } from "~/lib/aphrodite"
import {
    darken,
    getContrastColor,
    getContrastTheme,
    getOverlayColor,
    lighten,
} from "~/lib/utils"

export const DEFAULT_CUSTOMIZATION = {
    primaryColor: "#a8a8a8",
}

export const getters = {
    contrastColor: (state, getters) => getContrastColor(getters.primaryColor),
    contrastColorDark10: (state, getters) => darken(getters.contrastColor, 10),
    contrastTheme: (state, getters) => getContrastTheme(getters.primaryColor),

    overlayColor15: (state, getters) =>
        getOverlayColor(getters.primaryColor, 15),
    overlayColor50: (state, getters) =>
        getOverlayColor(getters.primaryColor, 50),
    overlayColor85: (state, getters) =>
        getOverlayColor(getters.primaryColor, 85),
    primaryColor: state => state.customization.primaryColor,
    primaryColorLight15: state => lighten(state.customization.primaryColor, 15),
    primaryColorLight25: state => lighten(state.customization.primaryColor, 25),
    primaryColorLight30: state => lighten(state.customization.primaryColor, 30),
    primaryColorLight50: state => lighten(state.customization.primaryColor, 50),
    primaryColorLight90: state => lighten(state.customization.primaryColor, 90),
    primaryColorDark10: state => darken(state.customization.primaryColor, 10),
    primaryColorDark15: state => darken(state.customization.primaryColor, 15),
    primaryColorDark30: state => darken(state.customization.primaryColor, 30),
    primaryColorOpacity10: state =>
        tinycolor(state.customization.primaryColor).setAlpha(0.1),

    styles: (state, getters) => {
        const {
            contrastColor,
            contrastColorDark10,
            overlayColor15,
            overlayColor50,
            overlayColor85,
            primaryColor,
            primaryColorLight15,
            primaryColorLight25,
            primaryColorLight30,
            primaryColorLight50,
            primaryColorLight90,
            primaryColorDark10,
            primaryColorDark15,
            primaryColorDark30,
            primaryColorOpacity10,
        } = getters

        return StyleSheet.create({
            // Backgrounds
            bgPrimary: {
                backgroundColor: `${primaryColor} !important`,
            },

            bgPrimaryDisabled: {
                backgroundColor: `${primaryColorLight15} !important`,
            },

            bgPrimaryLight: {
                backgroundColor: `${primaryColorLight90} !important`,
            },

            bgPrimaryLight25: {
                backgroundColor: `${primaryColorLight25} !important`,
            },

            bgPrimaryDark30: {
                backgroundColor: `${primaryColorDark30} !important`,
            },

            bgPrimaryOpacity10: {
                backgroundColor: `${primaryColorOpacity10} !important`,
            },

            bgNegative: {
                backgroundColor: `${contrastColor} !important`,
            },

            bgOverlay15: {
                backgroundColor: `${overlayColor15} !important`,
            },

            bgOverlay85: {
                backgroundColor: `${overlayColor85} !important`,
            },

            bgPrimaryOpacity10Hover: {
                ":hover": {
                    backgroundColor: `${primaryColorOpacity10} !important`,
                },
            },

            bgPrimaryOpacity10Focus: {
                ":focus": {
                    backgroundColor: `${primaryColorOpacity10} !important`,
                },
            },

            bgPrimaryFocus: {
                ":focus": {
                    backgroundColor: `${primaryColor} !important`,
                },
            },

            bgPrimaryHover: {
                ":hover": {
                    backgroundColor: `${primaryColor} !important`,
                },
            },

            customHoverPrimary: {
                ":hover": {
                    color: `${primaryColor} !important`,
                },
                ":focus": {
                    color: `${primaryColor} !important`,
                },
                ":active": {
                    color: `${primaryColor} !important`,
                },
            },

            // Links
            linkPrimary: {
                color: `${primaryColor} !important`,

                ":hover": {
                    color: `${primaryColorDark10} !important`,
                },
                ":focus": {
                    color: `${primaryColorDark10} !important`,
                },
                ":active": {
                    color: `${primaryColorDark30} !important`,
                },
            },

            linkPrimaryLoading: {
                color: `${primaryColorLight15} !important`,

                ":hover": {
                    color: `${primaryColorLight15} !important`,
                },
                ":focus": {
                    color: `${primaryColorLight15} !important`,
                },
                ":active": {
                    color: `${primaryColorLight15} !important`,
                },
            },

            linkNegative: {
                backgroundColor: `${primaryColor} !important`,
                color: `${contrastColor} !important`,

                ":hover": {
                    backgroundColor: `${primaryColorDark10} !important`,
                },
                ":focus": {
                    backgroundColor: `${primaryColorDark10} !important`,
                },
                ":active": {
                    backgroundColor: `${primaryColorDark30} !important`,
                },
            },

            innerLinkPrimary: {
                "& a": {
                    color: `${primaryColor} !important`,
                    ":hover": { color: `${primaryColorDark10} !important` },
                    ":focus": { color: `${primaryColorDark10} !important` },
                    ":active": { color: `${primaryColorDark30} !important` },
                },
            },

            // Text
            textPrimary: {
                color: `${primaryColor} !important`,
            },

            textPrimaryLight: {
                color: `${primaryColorLight15} !important`,
            },

            textPrimaryLight25: {
                color: `${primaryColorLight25} !important`,
            },

            textPrimaryLight30: {
                color: `${primaryColorLight30} !important`,
            },

            textPrimaryLight50: {
                color: `${primaryColorLight50} !important`,
            },

            textPrimaryDark: {
                color: `${primaryColorDark15} !important`,
            },

            focusTextPrimaryDark: {
                ":focus": {
                    color: `${primaryColorDark15} !important`,
                },
                ":active": {
                    color: `${primaryColorDark15} !important`,
                },
            },

            textNegative: {
                color: `${contrastColor} !important`,
            },

            textNegativeActive: {
                ":active": {
                    color: `${contrastColor} !important`,
                },
            },

            textNegativeFocus: {
                ":focus": {
                    color: `${contrastColor} !important`,
                },
            },

            textNegativeHover: {
                ":hover": {
                    color: `${contrastColor} !important`,
                },
            },

            textOverlay15: {
                color: `${overlayColor15} !important`,
            },

            textOverlay85: {
                color: `${overlayColor85} !important`,
            },

            bgPrimaryDark10Focus: {
                ":focus": {
                    backgroundColor: `${primaryColorDark10} !important`,
                },
            },

            bgPrimaryDark10Hover: {
                ":hover": {
                    backgroundColor: `${primaryColorDark10} !important`,
                },
            },

            textNegativeDark10Focus: {
                ":focus": {
                    color: `${contrastColorDark10} !important`,
                },
            },

            textNegativeDark10Hover: {
                ":hover": {
                    color: `${contrastColorDark10} !important`,
                },
            },

            bgPrimaryDark30Active: {
                ":active": {
                    backgroundColor: `${primaryColorDark30} !important`,
                },
            },

            // Borders
            borderPrimary: {
                borderColor: `${primaryColor} !important`,
            },

            borderPrimaryLight25: {
                borderColor: `${primaryColorLight25} !important`,
            },

            borderPrimaryActive: {
                ":active": {
                    borderColor: `${primaryColor} !important`,
                },
            },

            borderPrimaryFocus: {
                ":focus": {
                    borderColor: `${primaryColor} !important`,
                },
            },

            borderPrimaryFocusVisible: {
                ":focus-visible": {
                    outline: `5px solid ${primaryColor} !important`,
                },
            },

            ringPrimaryFocusVisible: {
                ":focus-visible": {
                    boxShadow: `rgb(248, 250, 252) 0px 0px 0px 2px, ${primaryColorLight50} 0px 0px 0px 5px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px;`,
                },
            },

            borderPrimaryHover: {
                ":hover": {
                    borderColor: `${primaryColor} !important`,
                },
            },

            borderNegative: {
                borderColor: `${contrastColor} !important`,
            },

            borderOverlay15: {
                borderColor: `${overlayColor15} !important`,
            },

            borderOverlay50Hover: {
                ":hover": {
                    borderColor: `${overlayColor50} !important`,
                },
            },

            borderPrimaryDark30Active: {
                ":active": {
                    borderColor: `${primaryColorDark30} !important`,
                },
            },

            activeNavPrimary: {
                color: primaryColor,
                borderBottom: `2px solid ${primaryColor}`,

                ":hover": {
                    color: `${primaryColorDark10} !important`,
                },
                ":focus": {
                    color: `${primaryColorDark10} !important`,
                },
            },

            formControlPrimary: {
                ":active": {
                    borderColor: `${primaryColor} !important`,
                },

                ":focus": {
                    borderColor: `${primaryColor} !important`,
                },
            },

            highlightPrimary: {
                position: "relative",

                "::after": {
                    borderRadius: "inherit",
                    content: '""',
                    display: "block",
                    height: "100%",
                    left: "0",
                    pointerEvents: "none",
                    position: "absolute",
                    top: "0",
                    border: "1px solid transparent",
                    transition: "border ease 0.2s",
                    width: "100%",
                },

                ":hover::after": {
                    border: `1px solid ${primaryColor}`,
                },

                ":focus::after": {
                    border: `1px solid ${primaryColor}`,
                },
            },

            fillPrimary: {
                fill: primaryColor,
            },

            fillNegative: {
                fill: contrastColor,
            },

            strokePrimary: {
                stroke: primaryColor,
            },

            strokePrimaryLight30: {
                stroke: primaryColorLight30,
            },
        })
    },
}

export const mutations = {
    setCustomization(state, customization) {
        merge(state.customization, customization)
    },
}

export const actions = {
    updateCustomization: ({ commit }, customization) => {
        commit("setCustomization", {
            primaryColor: customization.primaryColor,
        })
    },
}

export function makeDefaultState() {
    return {
        customization: Object.assign({}, DEFAULT_CUSTOMIZATION),
    }
}

export default {
    actions,
    getters,
    mutations,
    namespaced: true,
    state: makeDefaultState(),
}
